import { ArrowUpDown, FolderTree, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCountryScope, usePermissions } from '@/features/auth';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

import { useCategories, useDeleteCategory, useSetCategoryStatus } from '../api/categories.queries';
import { createCategoryColumns } from '../components/categories-table-columns';
import { CategoryFormDialog } from '../components/category-form-dialog';
import { CategoryOrderDialog } from '../components/category-order-dialog';
import type { Category } from '../types/category.types';

const ANY = 'all';
const REASON_MAX = 500;

/**
 * Categories — group brands on the storefront, in every country.
 *
 * Changing one needs catalogue.manage and all-country scope (the Admin API
 * refuses it otherwise). A category in use is deactivated, which takes its brands
 * off the storefront, and the confirmation says how many. Only a category no
 * brand belongs to can be deleted — the API refuses the rest.
 */
export function CategoriesPage() {
  const tableState = useTableSearchParams({ defaultSort: 'nameEn', defaultDirection: 'asc' });
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const canManage = can('catalogue.manage') && allCountries;

  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [reordering, setReordering] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<Category | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);
  const [reason, setReason] = useState('');

  const statusFilter = tableState.getFilter('status');
  const debouncedSearch = useDebouncedValue(tableState.search, 300);

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: debouncedSearch || undefined,
      isActive: statusFilter === null ? undefined : statusFilter === 'active',
      sort: tableState.sort ?? undefined,
      direction: tableState.direction,
    }),
    [
      tableState.page,
      tableState.pageSize,
      debouncedSearch,
      statusFilter,
      tableState.sort,
      tableState.direction,
    ],
  );

  const categories = useCategories(params);
  const setStatus = useSetCategoryStatus();
  const deleteCategory = useDeleteCategory();

  const columns = useMemo(
    () =>
      createCategoryColumns({
        canManage,
        onEdit: setEditing,
        onToggleActive: (category) => {
          setReason('');
          setPendingToggle(category);
        },
        onDelete: (category) => {
          setReason('');
          setPendingDelete(category);
        },
      }),
    [canManage],
  );

  const table = useDataTable({
    data: categories.data,
    columns,
    tableState,
    getRowId: (category) => String(category.id),
  });

  const toggleName = pendingToggle?.nameEn ?? 'This category';
  const deleteName = pendingDelete?.nameEn ?? pendingDelete?.nameAr ?? 'This category';

  return (
    <PageContainer>
      <PageHeader
        title="Categories"
        description="Group brands on the storefront. Categories are shared by every country."
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setReordering(true)}>
                <ArrowUpDown className="size-4" aria-hidden />
                Storefront order
              </Button>
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus className="size-4" aria-hidden />
                Add category
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
        >
          <SearchInput
            value={tableState.search}
            onChange={tableState.setSearch}
            placeholder="Search categories…"
            className="w-full sm:w-60"
          />
          <Select
            value={statusFilter ?? ANY}
            onValueChange={(value) => tableState.setFilter('status', value === ANY ? null : value)}
          >
            <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Filter by storefront">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Active or inactive
              </SelectItem>
              <SelectItem value="active" className="text-xs">
                Active
              </SelectItem>
              <SelectItem value="inactive" className="text-xs">
                Inactive
              </SelectItem>
            </SelectContent>
          </Select>
        </DataTableToolbar>

        <DataTable
          table={table}
          isLoading={categories.isLoading}
          isFetching={categories.isFetching}
          error={categories.error}
          onRetry={() => void categories.refetch()}
          emptyState={
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={FolderTree}
                title="No categories match your filters"
                description="Try a different search term, or clear the filters."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={FolderTree}
                title="No categories yet"
                description={
                  canManage
                    ? 'Add a category so brands can be grouped on the storefront.'
                    : 'No categories have been added yet.'
                }
              />
            )
          }
        />

        <DataTablePagination table={table} />
      </div>

      {editing !== null && (
        <CategoryFormDialog
          category={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => !open && setPendingToggle(null)}
        title={pendingToggle?.isActive ? 'Deactivate this category?' : 'Activate this category?'}
        description={
          pendingToggle?.isActive ? (
            <>
              <strong>{toggleName}</strong> and its <strong>{pendingToggle.brandCount}</strong>{' '}
              brand
              {pendingToggle.brandCount === 1 ? '' : 's'} will disappear from the storefront in
              every country. Existing orders are unaffected, and you can activate it again at any
              time.
            </>
          ) : (
            <>
              <strong>{toggleName}</strong> will appear on the storefront in every country, with its
              active brands.
            </>
          )
        }
        confirmLabel={pendingToggle?.isActive ? 'Deactivate' : 'Activate'}
        variant={pendingToggle?.isActive ? 'destructive' : 'default'}
        isPending={setStatus.isPending}
        onConfirm={() => {
          if (!pendingToggle) return;
          const trimmed = reason.trim();
          setStatus.mutate(
            {
              categoryId: pendingToggle.id,
              isActive: !pendingToggle.isActive,
              reason: trimmed === '' ? null : trimmed,
            },
            { onSuccess: () => setPendingToggle(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="category-change-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="category-change-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={REASON_MAX}
            disabled={setStatus.isPending}
            className="h-8 text-xs"
          />
        </div>
      </ConfirmDialog>
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this category?"
        description={
          <>
            <strong>{deleteName}</strong> has no brands. It will be removed permanently, with its
            English and Arabic names, from every country. This can't be undone.
          </>
        }
        confirmLabel="Delete"
        variant="destructive"
        isPending={deleteCategory.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const trimmed = reason.trim();
          deleteCategory.mutate(
            {
              categoryId: pendingDelete.id,
              name: deleteName,
              reason: trimmed === '' ? null : trimmed,
            },
            // Closed on a refusal too: the toast explains it and the counts refresh.
            { onSettled: () => setPendingDelete(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="category-delete-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="category-delete-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={REASON_MAX}
            disabled={deleteCategory.isPending}
            className="h-8 text-xs"
          />
        </div>
      </ConfirmDialog>
      {reordering && <CategoryOrderDialog onClose={() => setReordering(false)} />}
    </PageContainer>
  );
}
